import { observer } from 'mobx-react'
import styled from 'styled-components'
import { VehicleHealthStatus, VehicleMaintenance } from 'models/Vehicles/VehicleMaintenanceModel'
import { ExistingVehicle, ExistingVehicleModel, NewVehicleEditCloneModel, Vehicle } from 'models/Vehicles/VehicleModel'
import type { VehicleState } from 'models/Vehicles/VehicleStateModel'
import { getLinkForVehiclesPageWithSelectedVehicle, vehiclesPageWithCreateNewVehicleLink } from 'router'
import { svgNoShrinkClassName } from 'styles/globalCss'
import { theme } from 'styles/theme'
import { PrimaryButton } from '../Button/Button'
import { SvgIcons } from '../Icon/Icon'
import { VehicleStatusIndicator } from '../StatusIndicator/StatusIndicator'
import { Column, Row, TableTitle, TdAnchorForTrLink, TableTitleRow, ScrollableTable} from '../Table/Table'
import { Heading2, Heading3 } from '../Typography/Typography'

const VehicleCardContainer = styled(Row)<WithIsSelected>`
    position: relative;
    background-color: ${props => props.isSelected ? theme.colors.TransparentYellow : 'unset' };
    border: ${theme.borderWidths.thin} solid ${props => props.isSelected ? theme.colors.SemiDarkGrey : theme.colors.MediumGrey};
    border-radius: ${theme.radii.medium};
    padding: 1em;
    min-height: 6.5em; // fixed height of card, even in "new vehicle" card
    :hover{
        background-color: ${theme.colors.LightGrey};
    }
`
interface VehicleCardProps {
    vehicle: Vehicle
    isSelected: boolean
    showStatus?: boolean
    vehicleStatus?: VehicleState
    vehicleMaintenanceInfo?: VehicleMaintenance
}
const VehicleCard = observer(({ vehicle, isSelected, showStatus = true, vehicleStatus, vehicleMaintenanceInfo }:VehicleCardProps) => {
    const rowLink = ExistingVehicleModel.is(vehicle) ? getLinkForVehiclesPageWithSelectedVehicle(vehicle.id) : vehiclesPageWithCreateNewVehicleLink
    return (
    <VehicleCardContainer {...{ gap: "0", isSelected }}>
        <TdAnchorForTrLink {...{...rowLink}} />
        <Column style={{ flexGrow: 1}}>
            <Row style={{ justifyContent: 'space-between', alignContent: 'center' }}>
                <Row gap="0.5em" alignItems="center">
                <Heading2>{vehicle.name}</Heading2>
                <Heading3>({vehicle.id})</Heading3>
                </Row>
                <VehicleHealthIndicator {...{ vehicleMaintenanceInfo, style: { marginLeft: 'auto' } }} />
            </Row>
            <Row style={{ justifyContent: 'space-between', alignContent: 'center', height: '2em' }}>
                { showStatus && <VehicleStatusIndicator {...{ vehicleStatus : vehicleStatus?.combinedState }}/> }
                { vehicleStatus?.isParked && <VehicleParkedIndicator /> }
            </Row>
        </Column>
    </VehicleCardContainer>
)})

const VehicleParkedIndicator = () => 
    <span style={{marginLeft: 'auto', marginRight: '2px'}}>
        <SvgIcons.VehicleParkedSvg />
    </span>

const newVehicleTemplate = NewVehicleEditCloneModel.create({ name: 'New Vehicle ', center_id: 1})
const NewVehicleCard = () => <VehicleCard {...{
    vehicle: newVehicleTemplate,
    isSelected: true,
    showStatus: false,
}}/>

interface VehicleHealthIndicatorProps {
    vehicleMaintenanceInfo: VehicleMaintenance | undefined
}
const VehicleHealthIndicator = observer(({ vehicleMaintenanceInfo } : VehicleHealthIndicatorProps) => {
    if (vehicleMaintenanceInfo === undefined) {
        return null
    }
    switch (vehicleMaintenanceInfo.aggregateHealthStatus){
        case VehicleHealthStatus.HEALTHY:
            return null
        case VehicleHealthStatus.UNHEALTHY:
            return <SvgIcons.VehicleErrorSvg {...{ className: svgNoShrinkClassName, title: vehicleMaintenanceInfo.getHealthMessage, style: { zIndex: 3 } }}/>
        case VehicleHealthStatus.WARNING:
            return <SvgIcons.VehicleWarningSvg {...{ className: svgNoShrinkClassName, title: vehicleMaintenanceInfo.getHealthMessage, style: { zIndex: 3 } }}/>
    }  
})

const VehicleTableContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1em;
    min-height: 0;
    overflow: auto;
`

interface VehiclesTableProps {
    vehicles: Array<ExistingVehicle>
    vehicleStates: Map<string, VehicleState>
    isAddingVehicle: boolean, 
    vehicleMaintenance: Map<string, VehicleMaintenance>
    selectedVehicleId: number | null 
}

export const VehiclesTable = observer(({ vehicles, vehicleMaintenance, isAddingVehicle, selectedVehicleId, vehicleStates }:VehiclesTableProps) => {
    return (
        <>
            <TableTitleRow>
                <TableTitle {...{ 
                    entityCount: vehicles.length, 
                    entityName: 'vehicle', 
                    style: { marginRight: 'auto' },
                }} />
                <PrimaryButton {...{
                    text: 'Add vehicle',
                    startComponent: <SvgIcons.PlusSvg />,
                    ...vehiclesPageWithCreateNewVehicleLink,
                }}/>
            </TableTitleRow>
            <ScrollableTable>
                <VehicleTableContainer>
                    {
                        isAddingVehicle && <NewVehicleCard />
                    }
                    {   vehicles.map((vehicle) => <VehicleCard {...{
                            key: vehicle.id,
                            vehicle, 
                            isSelected: vehicle.id === selectedVehicleId,
                            vehicleStatus: vehicleStates.get(vehicle.id.toString()),
                            vehicleMaintenanceInfo: vehicleMaintenance.get(vehicle.id.toString()), 
                        }} />)
                    }
                </VehicleTableContainer>
            </ScrollableTable>
        </>)
})